
import { DeepAnalysisResult } from '@/types/analysisTypes';

/**
 * Extracts sub-scores from analysis result
 */
export function getSubScores(result: DeepAnalysisResult) {
  const scores: Array<{ title: string; score: number; description: string }> = [];

  if (!result || !result.category_scores) return scores;

  console.log("🧪 SubScores keys:", Object.keys(result.category_scores));

  // Debug before processing
  console.log("🔥 Before processing - all category scores:");
  for (const [key, value] of Object.entries(result.category_scores)) {
    console.log(`🔍 Key: ${key}, Valid: ${isScoreValid(key, value)}, Label: ${formatCategoryName(key)}, Value:`, value);
  }

  // Process each category in the category_scores object dynamically
  Object.entries(result.category_scores).forEach(([key, value]) => {
    // Exclude AI Readiness keys since they're handled by dedicated component
    if (key === 'ai_readiness_proxy' || key === 'ai_optimization_score') {
      console.log(`🔍 getSubScores SKIPPING ${key}: handled by dedicated AI Readiness component`);
      return;
    }
    
    // Dynamic validation only - no static exclusions
    const valid = isScoreValid(key, value);
    const label = formatCategoryName(key);
    
    console.log(`🔍 getSubScores processing ${key}: valid=${valid}, label="${label}", value:`, value);
    
    // Special debugging for AI-related keys
    if (key.includes('ai_') || key.includes('AI')) {
      console.log(`🤖 AI KEY FOUND in getSubScores: ${key}`, {
        valid,
        label,
        value,
        type: typeof value
      });
    }
    
    // Ensure we don't early return for missing labels - only invalid data
    if (!valid) {
      console.log(`🔍 getSubScores SKIPPING ${key}: invalid data`);
      return;
    }
    
    if (!label) {
      console.log(`🔍 getSubScores SKIPPING ${key}: no label mapping`);
      return;
    }

    let score = 0;
    let description = '';

    // Handle both old and new format
    if (typeof value === 'number') {
      score = value;
    } else if (typeof value === 'object' && value !== null) {
      score = value.score || 0;
      description = value.description || '';
    }

    const scoreItem = {
      title: label,
      score,
      description: description || getDefaultDescription(key)
    };

    scores.push(scoreItem);
    
    console.log(`✅ Added detailed score: ${label} = ${score}/20`, scoreItem);
    
    // Special debugging for AI Readiness
    if (label === 'AI Readiness') {
      console.log(`🎯 AI Readiness score item created:`, scoreItem);
    }
  });

  console.log('📊 Detailed scores received:', scores.map(s => s.title));
  console.log('🔍 Final subScores:', scores);

  return scores;
}

/**
 * Validate if a score is valid for display - handle string numbers and zero scores
 */
function isScoreValid(key: string, value: any): boolean {
  if (!key || key.trim() === '') return false;
  if (value === undefined || value === null) return false;
  
  // Handle both number and string numbers, allow zero scores
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  
  // Handle object with score property
  if (typeof value === 'object' && value.score !== undefined) {
    return Number.isFinite(value.score);
  }
  
  // Handle stringified numbers
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return !isNaN(parsed) && Number.isFinite(parsed);
  }
  
  return false;
}

/**
 * Format a category key into a readable name
 */
function formatCategoryName(key: string): string {
  if (!key) return '';
  
  // Centralized label mapping
  const SCORE_LABELS: Record<string, string> = {
    content_quality: 'Content Quality',
    EEAT: 'E-E-A-T',
    helpfulness: 'Helpfulness',
    shareability: 'Shareability',
    ai_optimization_score: 'AI Readiness',
    ai_readiness_proxy: 'AI Readiness'
  };
  
  // Use explicit mapping first
  if (SCORE_LABELS[key]) {
    return SCORE_LABELS[key];
  }
  
  // Fallback to formatted key name
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Get default descriptions for categories
 */
function getDefaultDescription(key: string): string {
  const descriptions: Record<string, string> = {
    content_quality: 'Evaluation of content depth, uniqueness, and overall quality',
    EEAT: 'Assessment of Experience, Expertise, Authoritativeness, and Trustworthiness',
    helpfulness: 'How well the content serves user intent and provides practical value',
    shareability: 'Social media potential and differentiation from similar content',
    ai_optimization_score: 'How well content is optimized for AI processing and indexing',
    ai_readiness_proxy: 'How well content is optimized for AI processing and indexing'
  };

  return descriptions[key] || 'Score analysis';
}

/**
 * Format a score for display (adds + if positive)
 */
export function formatScoreChange(value: number | string): string {
  if (typeof value === 'string') {
    return value;
  }
  
  if (value > 0) {
    return `+${value}`;
  }
  
  return value.toString();
}

/**
 * Helper function to download CSV data
 */
export function downloadCSV(csvContent: string, fileName: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  // Create a URL for the blob
  const url = URL.createObjectURL(blob);
  
  // Set up the download link
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  
  // Add the link to the DOM
  document.body.appendChild(link);
  
  // Trigger the download
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get the numeric value from a score that could be either a number or an object with a score property
 */
export function getScoreValue(score: number | { score: number, description?: string }): number {
  if (typeof score === 'number') {
    return score;
  }
  return score.score;
}

/**
 * Get the description from a score that could be either a number or an object with a description property
 */
export function getScoreDescription(score: number | { score: number, description?: string }): string {
  if (typeof score === 'number') {
    return '';
  }
  return score.description || '';
}

/**
 * Get audience shareability score value safely
 */
export function getAudienceScore(audienceData: number | { score: number, explanation: string, improvement: string }): number {
  if (typeof audienceData === 'number') {
    return audienceData;
  }
  return audienceData.score;
}

/**
 * Get audience explanation safely
 */
export function getAudienceExplanation(audienceData: number | { score: number, explanation: string, improvement: string }): string {
  if (typeof audienceData === 'number') {
    return '';
  }
  return audienceData.explanation || '';
}

/**
 * Get audience improvement safely
 */
export function getAudienceImprovement(audienceData: number | { score: number, explanation: string, improvement: string }): string {
  if (typeof audienceData === 'number') {
    return '';
  }
  return audienceData.improvement || '';
}
